import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Modal,
  Result,
  Space,
  Table,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SyncOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { AdminLocationSummary, AllyAdmin } from '@goelectric/shared-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { LocationStatusBadge, LocationSyncBadge } from '@/features/locaciones/components/LocationBadges';
import { useLocations } from '@/features/locaciones/hooks/useLocations';
import { canWriteAliados } from '@/layouts/navigation';
import { ApiError } from '@/lib/http/apiClient';
import { AllyAdminRows } from '../components/AllyAdminsFields';
import { AllyAdminStatusBadge, AllyStatusBadge, AllyTenantSyncBadge } from '../components/AllyBadges';
import { InviteLinksModal, type InvitedAdmin } from '../components/InviteLinksModal';
import { EditAllyModal } from '../components/EditAllyModal';
import { allyApi } from '../api/allyApi';
import { useAlly } from '../hooks/useAlly';
import { describeAllyApiError } from '../utils/allyErrors';
import type { CreateAllyAdminState } from '../utils/fiscal';
import {
  DOCUMENT_TYPE_LABELS,
  PERSON_TYPE_LABELS,
  allyDisplayName,
  formatAllyDocument,
  formatDateTime,
} from '../utils/format';

/** Estado del modal de links de invitación reusado del alta (F11). */
interface LinksModalState {
  admins: InvitedAdmin[];
  title: string;
  lead: string;
}

/**
 * Tope de ubicaciones traídas para la card del aliado. ponytail: sin paginación (la card
 * muestra todas de una); si un aliado superara las 100 ubicaciones, paginar o subir el tope.
 */
const ALLY_LOCATIONS_LIMIT = 100;

/**
 * Detalle de Aliado (US-2.2, `/aliados/:allyId`). Datos fiscales, comisión, estado y
 * sincronización. La sección de administradores muestra nombre, email y estado, y ofrece
 * las acciones F11: "Agregar administradores" (para aliados creados sin ellos) y
 * "Regenerar invitación" por admin en `PENDING`. Los links se muestran con el mismo modal
 * del alta. El reintento de sincronización se ofrece solo a roles con escritura y con el
 * estado en `PENDIENTE`/`ERROR`. Sobre el estado del aliado se ofrece "Desactivar" (DELETE,
 * con confirmación) en `ACTIVE` y "Reactivar" (PATCH `status: ACTIVE`) en `INACTIVE`.
 *
 * La card "Ubicaciones" cierra el círculo aliado → ubicaciones (el detalle de cada una ya
 * encadena a sus cargadores): las lista filtradas por `allyId` con estado y sincronización, y
 * cada fila navega al detalle de la ubicación.
 */
export function AllyDetailPage() {
  const { allyId } = useParams<{ allyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { message, modal } = App.useApp();
  const { ally, loading, error, notFound, refresh } = useAlly(allyId);
  // Las ubicaciones se filtran por `allyId` en el backend (el contrato ya lo soporta). El
  // detalle del aliado lo ven los mismos roles que el listado de ubicaciones (ALIADOS_ROLES
  // === LOCACIONES_ROLES en `navigation.tsx`), así que la card no necesita gate.
  const {
    data: allyLocations,
    meta: locationsMeta,
    loading: locationsLoading,
    error: locationsError,
    refresh: refreshLocations,
  } = useLocations({ limit: ALLY_LOCATIONS_LIMIT, initialFilters: { allyId } });
  const canWrite = canWriteAliados(user?.role);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const [addForm] = Form.useForm<{ admins?: CreateAllyAdminState[] }>();
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [links, setLinks] = useState<LinksModalState | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const goToList = () => navigate('/aliados');

  /**
   * Ejecuta un cambio de estado del aliado (desactivar/reactivar) mostrando el mensaje
   * real del backend y refrescando el detalle. Comparte el estado `statusSubmitting` para
   * bloquear ambos botones mientras corre la petición.
   */
  const changeStatus = async (
    action: () => Promise<unknown>,
    successMessage: string,
    fallbackMessage: string,
  ) => {
    if (!allyId) return;
    setStatusSubmitting(true);
    try {
      await action();
      message.success(successMessage);
      refresh();
    } catch (caught) {
      message.error(describeAllyApiError(caught, fallbackMessage).message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  /** Desactivar es destructivo (corta la operación del aliado): pide confirmación explícita. */
  const handleDeactivate = () => {
    if (!allyId || !ally) return;
    modal.confirm({
      title: 'Desactivar aliado',
      content: `${allyDisplayName(ally)} dejará de operar y de liquidar comisión. El historial se conserva y puedes reactivarlo cuando quieras.`,
      okText: 'Desactivar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: () =>
        changeStatus(
          () => allyApi.remove(allyId),
          'El aliado quedó inactivo.',
          'No se pudo desactivar el aliado.',
        ),
    });
  };

  /** Reactivar no tiene endpoint propio: el PATCH parcial acepta `status: 'ACTIVE'`. */
  const handleReactivate = () => {
    if (!allyId) return;
    void changeStatus(
      () => allyApi.update(allyId, { status: 'ACTIVE' }),
      'El aliado quedó activo.',
      'No se pudo reactivar el aliado.',
    );
  };

  const handleSync = async () => {
    if (!allyId) return;
    setSyncing(true);
    setSyncError(null);
    try {
      await allyApi.sync(allyId);
      message.success('Sincronización reintentada. El aliado se actualizará en breve.');
      refresh();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 502) {
        setSyncError(
          'El core no respondió: no se pudo crear el tenant. El aliado sigue en estado de error; puedes reintentar.',
        );
      } else {
        setSyncError(describeAllyApiError(caught).message);
      }
    } finally {
      setSyncing(false);
    }
  };

  /** Abre el modal de agregar admins con una fila vacía y sin errores previos. */
  const openAddModal = () => {
    setAddError(null);
    addForm.resetFields();
    setAddOpen(true);
  };

  const handleAddAdmins = async (values: { admins?: CreateAllyAdminState[] }) => {
    if (!allyId) return;
    const admins = (values.admins ?? [])
      .map((admin) => ({ name: admin.name?.trim() ?? '', email: admin.email?.trim() ?? '' }))
      .filter((admin) => admin.name !== '' && admin.email !== '');
    // El contrato exige `minItems: 1`; las filas incompletas ya las frenan las reglas por fila.
    if (admins.length === 0) {
      setAddError('Agrega al menos un administrador.');
      return;
    }
    setAddSubmitting(true);
    setAddError(null);
    try {
      const created = await allyApi.addAdmins(allyId, { admins });
      const invited = created.filter((admin): admin is InvitedAdmin => Boolean(admin.inviteUrl));
      setAddOpen(false);
      refresh();
      if (invited.length > 0) {
        setLinks({
          admins: invited,
          title: 'Administradores agregados',
          lead: 'El link de invitación debe enviarse a cada administrador para que realicen su primer ingreso.',
        });
      } else {
        message.success('Administradores agregados.');
      }
    } catch (caught) {
      setAddError(describeAllyApiError(caught, 'No se pudieron agregar los administradores.').message);
    } finally {
      setAddSubmitting(false);
    }
  };

  /** Regenera la invitación de un admin `PENDING` y muestra el link nuevo (el anterior queda invalidado). */
  const handleReenroll = (admin: AllyAdmin) => {
    if (!allyId) return;
    modal.confirm({
      title: 'Regenerar invitación',
      content: `Se invalidará el link anterior de ${admin.name ?? admin.email} y se generará uno nuevo.`,
      okText: 'Regenerar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const updated = await allyApi.reenrollAdmin(allyId, admin.userId);
          if (updated.inviteUrl) {
            setLinks({
              admins: [{ ...updated, inviteUrl: updated.inviteUrl }],
              title: 'Invitación regenerada',
              lead: 'El link anterior quedó invalidado. Debe enviarse el nuevo link al administrador.',
            });
          }
        } catch (caught) {
          message.error(
            describeAllyApiError(caught, 'No se pudo regenerar la invitación.').message,
          );
        }
      },
    });
  };

  const adminColumns: ColumnsType<AllyAdmin> = [
    { title: 'Nombre', dataIndex: 'name', key: 'name', render: (name?: string) => name ?? '—' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (_, admin) => <AllyAdminStatusBadge status={admin.status} />,
    },
    ...(canWrite
      ? [
          {
            title: 'Acciones',
            key: 'actions',
            render: (_: unknown, admin: AllyAdmin) =>
              admin.status === 'PENDING' ? (
                <Button
                  type="link"
                  icon={<ReloadOutlined />}
                  onClick={() => handleReenroll(admin)}
                  style={{ paddingInline: 0 }}
                >
                  Regenerar invitación
                </Button>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          } satisfies ColumnsType<AllyAdmin>[number],
        ]
      : []),
  ];

  const locationColumns: ColumnsType<AdminLocationSummary> = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, location) => (
        <Link to={`/ubicaciones/${encodeURIComponent(location.id)}`}>{name}</Link>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (_, location) => <LocationStatusBadge status={location.status} />,
    },
    {
      title: 'Sincronización',
      dataIndex: 'syncStatus',
      key: 'syncStatus',
      render: (_, location) => <LocationSyncBadge status={location.syncStatus} />,
    },
  ];

  if (loading) {
    return <Card loading title="Aliado" />;
  }

  if (notFound) {
    return (
      <Result
        status="404"
        title="Aliado no encontrado"
        subTitle="El aliado no existe o fue eliminado."
        extra={<Button onClick={goToList}>Volver al listado</Button>}
      />
    );
  }

  if (error || !ally) {
    return (
      <Result
        status="error"
        title="No se pudo cargar el aliado"
        subTitle={error ?? undefined}
        extra={
          <Space>
            <Button type="primary" onClick={refresh}>
              Reintentar
            </Button>
            <Button onClick={goToList}>Volver al listado</Button>
          </Space>
        }
      />
    );
  }

  return (
    <div className="aliados-page">
      <div className="aliados-page__header">
        <Space align="center" wrap>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de aliados"
            onClick={goToList}
          />
          <Typography.Title level={3} style={{ margin: 0 }}>
            {allyDisplayName(ally)}
          </Typography.Title>
          <AllyStatusBadge status={ally.status} />
          <AllyTenantSyncBadge status={ally.tenantSyncStatus} />
        </Space>
        <Space>
          {canWrite &&
            (ally.tenantSyncStatus === 'PENDIENTE' || ally.tenantSyncStatus === 'ERROR') && (
              <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
                Reintentar sincronización
              </Button>
            )}
          {canWrite && (
            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
              Editar
            </Button>
          )}
          {canWrite && ally.status === 'ACTIVE' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={statusSubmitting}
              onClick={handleDeactivate}
            >
              Desactivar
            </Button>
          )}
          {canWrite && ally.status === 'INACTIVE' && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={statusSubmitting}
              onClick={handleReactivate}
            >
              Reactivar
            </Button>
          )}
        </Space>
      </div>

      {syncError && (
        <Alert type="error" showIcon role="alert" message="No se pudo sincronizar" description={syncError} />
      )}

      <Card title="Identidad fiscal">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Tipo de persona">
            {PERSON_TYPE_LABELS[ally.personType]}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo de documento">
            {DOCUMENT_TYPE_LABELS[ally.documentType]}
          </Descriptions.Item>
          <Descriptions.Item label="Documento">
            <span style={{ fontFamily: 'var(--ge-font-mono)' }}>{formatAllyDocument(ally)}</span>
          </Descriptions.Item>
          {ally.personType === 'JURIDICA' ? (
            <Descriptions.Item label="Razón social">{ally.legalName}</Descriptions.Item>
          ) : (
            <>
              <Descriptions.Item label="Nombres">{ally.firstName}</Descriptions.Item>
              <Descriptions.Item label="Apellidos">{ally.lastName}</Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Email">{ally.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="Teléfono">{ally.phone ?? '—'}</Descriptions.Item>
        </Descriptions>

        {ally.documentType !== 'NIT' && (
          <Typography.Text type="secondary">
            El dígito de verificación no aplica a {DOCUMENT_TYPE_LABELS[ally.documentType]}.
          </Typography.Text>
        )}
      </Card>

      <Card title="Liquidación y tenant">
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label="Comisión">{ally.commissionRatePct}%</Descriptions.Item>
          <Descriptions.Item label="Estado">
            <AllyStatusBadge status={ally.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Sincronización del tenant">
            <AllyTenantSyncBadge status={ally.tenantSyncStatus} />
          </Descriptions.Item>
          <Descriptions.Item label="Ref. cuenta bancaria">
            <Typography.Text type="secondary">{ally.bankAccountRef ?? '—'}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="Creado">{formatDateTime(ally.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="Actualizado">{formatDateTime(ally.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Ubicaciones"
        extra={
          !locationsLoading && !locationsError ? (
            <Typography.Text type="secondary">
              {locationsMeta.total} {locationsMeta.total === 1 ? 'ubicación' : 'ubicaciones'}
            </Typography.Text>
          ) : undefined
        }
      >
        {locationsError ? (
          <Alert
            type="error"
            showIcon
            role="alert"
            message="No se pudieron cargar las ubicaciones"
            description={locationsError}
            action={
              <Button size="small" onClick={refreshLocations}>
                Reintentar
              </Button>
            }
          />
        ) : (
          <Table<AdminLocationSummary>
            rowKey="id"
            size="small"
            loading={locationsLoading}
            columns={locationColumns}
            dataSource={allyLocations}
            pagination={false}
            rowClassName="locaciones-row"
            onRow={(location) => ({
              onClick: () => navigate(`/ubicaciones/${encodeURIComponent(location.id)}`),
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Este aliado todavía no tiene ubicaciones."
                />
              ),
            }}
          />
        )}
      </Card>

      <Card
        title="Administradores del aliado"
        extra={
          canWrite ? (
            <Button type="primary" icon={<UserAddOutlined />} onClick={openAddModal}>
              Agregar administradores
            </Button>
          ) : undefined
        }
      >
        <Table<AllyAdmin>
          rowKey="userId"
          size="small"
          columns={adminColumns}
          dataSource={ally.admins ?? []}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="El aliado todavía no tiene administradores. Sin ellos, nadie puede ingresar a su panel."
              />
            ),
          }}
        />
      </Card>

      <Modal
        open={addOpen}
        title="Agregar administradores"
        onCancel={() => setAddOpen(false)}
        onOk={() => addForm.submit()}
        okText="Agregar"
        cancelText="Cancelar"
        confirmLoading={addSubmitting}
        forceRender
        width={640}
      >
        {addError && (
          <Alert
            type="error"
            showIcon
            role="alert"
            message="No se pudieron agregar los administradores"
            description={addError}
            style={{ marginBottom: 16 }}
          />
        )}
        <Typography.Paragraph type="secondary">
          A cada administrador se le genera un link de invitación para su primer ingreso.
        </Typography.Paragraph>
        <Form
          form={addForm}
          layout="vertical"
          initialValues={{ admins: [{}] }}
          onFinish={handleAddAdmins}
        >
          <AllyAdminRows />
        </Form>
      </Modal>

      <InviteLinksModal
        open={links !== null}
        admins={links?.admins ?? []}
        onClose={() => setLinks(null)}
        title={links?.title}
        lead={links?.lead}
      />

      <EditAllyModal
        open={editOpen}
        ally={ally}
        onClose={() => setEditOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}
